<template>
  <div class="sales-contact-finder">
    <Loading v-if="loading" />
    <template v-else>
      <!-- the form for selecting your contact -->
      <div class="sales-contact-finder__inputs">
        <!-- intro text -->
        <div class="sales-contact-finder__intro">
          {{ t.introText }}
        </div>

        <!-- form/component -->
        <div class="sales-contact-finder__form">
          <!-- Sales contact form. -->
          <div
            class="sales-contact-finder__select sales-contact-widget__select">
            <SalesContactForm
              :lang="lang"
              :defaultCountryId="preselectedCountryId"
              :currentCountryId="currentCountryId"
              @updated="updateContact"
              :loading="loading"
              @onLoadingStop="loadingStop" />
          </div>
        </div>
      </div>

      <!-- globe image&result -->
      <div :class="resultClasses">
        <!-- background image -->
        <div v-if="backgroundImage" class="sales-contact-finder__image">
          <picture class="responsive-image">
            <!-- has empty alt, bc it's decorative image only -->
            <img :src="backgroundImage" alt="" class="responsive-image__img" />
          </picture>
        </div>

        <!-- result -->
        <!-- we are rendering this section even if there are no results,
        bc we need to indicate this in the component itself
        to prevent code duplication -->
        <div class="sales-contact-finder__result">
          <p v-if="!objectEmpty(result)" class="sales-contact-finder__title">
            {{ t.salesContactTitle }}
          </p>

          <SalesContactResult
            :lang="lang"
            type="finder"
            :result="result"
            :noResults="salesContactMissing"
            :noResultsText="noResultsText"
            :transitionKey="`${transitionKey}`" />
        </div>

        <!-- reset/close button -->
        <Transition name="sales-contact-finder-slide-in" mode="out-in" appear>
          <button
            v-if="!objectEmpty(result) || salesContactMissing"
            class="sales-contact-finder__close"
            @click="reset"></button>
        </Transition>
      </div>
    </template>
  </div>
</template>

<script>
// components
import VueScrollTo from 'vue-scrollto';
import Loading from '../elements/Loading';
import SalesContactForm from './SalesContactForm';
import SalesContactResult from './SalesContactResult';
// utils
import translationStrings from '../../translations/translationStrings';
import { getSalesContact } from '../../utils/api';
import { getState, removeState } from '../../utils/persistedState';
import { isMobile } from '../../utils/isMobile';
import { VUESCROLLTO_OPTIONS } from '../../utils/constants';

export default {
  components: {
    Loading,
    SalesContactForm,
    SalesContactResult,
  },
  props: {
    lang: {
      required: true,
      type: String,
    },
    backgroundImage: {
      type: String,
      required: true,
    },
    noResultsText: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentCountryId: 0,
      loading: true,
      localStorageData: null,
      onMobile: isMobile(),
      preselectedCountryId: 0,
      result: {},
      salesContactMissing: false,
      t: translationStrings,
      transitionKey: '0',
    };
  },
  computed: {
    resultClasses() {
      // takes care of the position of the globe/styling when results are showing
      const baseClass = 'sales-contact-finder__results';
      return [
        baseClass,
        !this.salesContactMissing
          ? `${baseClass}--${
              this.objectEmpty(this.result) ? 'default' : 'result'
            }`
          : `${baseClass}--no-results`,
      ];
    },
  },
  watch: {
    result(newResult) {
      if (this.objectEmpty(newResult)) {
        // remove from local storage
        removeState();
        // reset the local storage variable
        this.localStorageData = null;
      }
    },
  },
  async mounted() {
    // Check if local storage holds a sales contact.
    await this.loadContactFromLocal();

    this.salesContactMissing = false;

    window.addEventListener('resize', () => {
      this.onMobile = isMobile();
    });
  },
  methods: {
    async fetchSingleSalesContact(id) {
      // @todo this function is used also on salesContactWidget, dry it
      try {
        const { data } = await getSalesContact(this.lang, id);
        this.result = data;
      } catch ({ response }) {
        if (response) {
          console.error(response);
        }
      } finally {
        this.loadingStop();
      }
    },
    async loadContactFromLocal() {
      // @todo this function is used also on salesContactWidget, dry it
      this.localStorageData = getState();
      // get the data from the local storage if it exists
      if (this.localStorageData && this.objectEmpty(this.result)) {
        await this.fetchSingleSalesContact(this.localStorageData?.id);

        // Update default selection for modal that might be opened in future.
        this.preselectedCountryId = this.localStorageData.country;
      }

      this.loadingStop();
    },
    updateContact({ result, countryId }) {
      // reset result, bc we're getting a new one ...
      // (but this doesn't trigger the result transition)
      this.reset(false);

      // ... and assign it the new one
      if (!this.objectEmpty(result)) {
        this.result = result;
      } else {
        // if payload is empty, then that means the sales contacts
        // for chosen parameters don't exist
        this.salesContactMissing = true;
      }

      this.currentCountryId = countryId;
      // update transition key so the transition triggers
      this.transitionKey = this.getTransitionKey(this.result || {});

      this.$nextTick(() => {
        if (this.onMobile) {
          VueScrollTo.scrollTo(
            '.sales-contact-finder__result',
            VUESCROLLTO_OPTIONS,
          );
        }
      });
    },
    getTransitionKey(result) {
      // needs a number that updates, bc transition
      // must change every time there's a input change
      return `${this.currentCountryId}-${this.salesContactMissing}-${
        result.id || 0
      }-${new Date().getTime() / 1000}`;
    },
    reset(scroll = true) {
      this.currentCountryId = 0;
      this.result = {};
      this.salesContactMissing = false;
      // scroll back to the form, but only if you click on X
      if (scroll && this.onMobile) {
        VueScrollTo.scrollTo(
          '.sales-contact-finder__inputs',
          VUESCROLLTO_OPTIONS,
        );
      }
    },
    loadingStop(waiting) {
      this.loading = waiting;
    },
    objectEmpty(obj) {
      return obj == null ? true : Object.keys(obj).length === 0;
    },
  },
};
</script>
